/**
 * The account button in the header, and the two dialogs behind it.
 *
 * Signing in is optional here and the panel is built around that. The viewer stores
 * models in the browser and needs no server at all, so if the account service is not
 * configured or cannot be reached, the button stays hidden and everything else carries on
 * working. An account that cannot be used is worse than no account button at all.
 */

import {
  ApiError,
  type Account,
  type AuthApi,
  UnreachableError,
} from '../api/authApi.js';
import { describeMoment } from '../format.js';
import { logger } from '../log.js';
import {
  buttonElement,
  dialogElement,
  element,
  formElement,
  inputElement,
} from './dom.js';

const log = logger('accounts');

/** Signing in and creating an account share one form; this is which one it is showing. */
type Mode = 'signIn' | 'register';

export interface AuthPanelOptions {
  readonly api: AuthApi;
  /** Where the API is. Only used to say so if it cannot be reached. */
  readonly apiBaseUrl: string;
  /** Told whenever the signed-in account changes, so the VR menu can follow along. */
  readonly onAccountChange: (account: Account | undefined) => void;
}

export class AuthPanel {
  private readonly button = buttonElement('account-button');

  private readonly authDialog = dialogElement('auth-dialog');
  private readonly authForm = formElement('auth-form');
  private readonly authTitle = element('auth-title');
  private readonly authIntro = element('auth-intro');
  private readonly emailField = inputElement('auth-email');
  private readonly passwordField = inputElement('auth-password');
  private readonly passwordHint = element('auth-password-hint');
  private readonly authMessage = element('auth-message');
  private readonly authSubmit = buttonElement('auth-submit');
  private readonly authCancel = buttonElement('auth-cancel');
  private readonly switchPrompt = element('auth-switch-prompt');
  private readonly switchButton = buttonElement('auth-switch');

  private readonly accountDialog = dialogElement('account-dialog');
  private readonly accountEmail = element('account-email');
  private readonly accountCreated = element('account-created');
  private readonly previousRow = element('account-previous-row');
  private readonly previousLogin = element('account-previous');
  private readonly accountLogins = element('account-logins');
  private readonly accountMessage = element('account-message');
  private readonly signOutButton = buttonElement('account-signout');
  private readonly accountClose = buttonElement('account-close');

  private account: Account | undefined;
  private mode: Mode = 'signIn';
  private busy = false;

  /**
   * The sign-in before the current one, as reported when signing in.
   *
   * Kept only for this page's lifetime. After a reload it is unknown, and the panel says
   * nothing rather than guessing from the history — the most recent entry there could
   * equally be a sign-in from another device.
   */
  private previous: string | undefined;

  constructor(private readonly options: AuthPanelOptions) {
    this.button.addEventListener('click', () => {
      if (this.account) {
        this.openAccount();
      } else {
        this.openAuth('signIn');
      }
    });

    this.authForm.addEventListener('submit', (event) => {
      event.preventDefault();
      void this.submit();
    });

    this.switchButton.addEventListener('click', () => {
      this.setMode(this.mode === 'signIn' ? 'register' : 'signIn');
    });

    this.authCancel.addEventListener('click', () => {
      this.authDialog.close();
    });
    this.accountClose.addEventListener('click', () => {
      this.accountDialog.close();
    });

    this.signOutButton.addEventListener('click', () => {
      void this.signOut();
    });

    // Clearing on close rather than on open means a password is not left sitting in the
    // DOM after the dialog is dismissed.
    this.authDialog.addEventListener('close', () => {
      this.authForm.reset();
      this.authMessage.textContent = '';
    });
  }

  /**
   * Find out whether anyone is signed in, and reveal the button if the service answers.
   *
   * Never rejects. A viewer whose account service is down should still get a working
   * viewer, so an unreachable service is reported to the console and otherwise ignored.
   */
  async start(): Promise<void> {
    const started = performance.now();

    try {
      const account = await this.options.api.currentAccount();
      this.setAccount(account);
      this.button.hidden = false;

      // The success case is logged as well as the failure, because a service that was
      // never asked and one that answered look exactly alike from the page: in both cases
      // nothing happens. The elapsed time is here because a service that answers in eight
      // seconds is a different problem from one that does not answer, and only this line
      // would show it.
      log.info(
        `${this.options.apiBaseUrl} answered in ${elapsed(started)}; ` +
          `${account === undefined ? 'nobody is signed in' : `signed in as ${account.email}`}.`,
      );
    } catch (error) {
      if (error instanceof UnreachableError) {
        this.explainUnreachable(error, started);
        return;
      }
      log.error('The account service answered with something unusable.', error);
    }
  }

  /**
   * Say, as usefully as possible, why there is no account button.
   *
   * The browser will not tell a page apart a server that is down from one that refused it
   * on CORS grounds: both surface as the same opaque `TypeError` from `fetch`, with the
   * real reason left in the console for a human. So the two cannot be distinguished here,
   * and the message names both possibilities and the values needed to check either.
   *
   * A warning rather than an error: running with no accounts is a supported arrangement.
   * But reaching *here* means an API was configured and did not answer, which is a
   * misconfiguration rather than a choice — and the symptom, a button that silently never
   * appears, gives nobody anything to go on otherwise.
   */
  private explainUnreachable(error: UnreachableError, started: number): void {
    const origin = window.location.origin;

    log.warn(
      `Accounts are unavailable: ${this.options.apiBaseUrl} did not answer a request ` +
        `from ${origin} after ${elapsed(started)}. The viewer works without them.\n` +
        `A cross-origin refusal looks exactly like an unreachable server from here, so ` +
        `if the service is running, check that PREVIEW_ALLOWED_ORIGINS lists ${origin} ` +
        `exactly — scheme, host and port, no trailing slash. A page opened by IP address ` +
        `is a different origin from the same page opened as localhost.`,
      error,
    );
  }

  private setAccount(account: Account | undefined): void {
    this.account = account;
    this.button.textContent = account ? account.email : 'Sign in';
    this.button.title = account ? 'View your account' : 'Sign in or create an account';
    this.options.onAccountChange(account);
  }

  private openAuth(mode: Mode): void {
    this.setMode(mode);
    this.authMessage.textContent = '';
    this.authDialog.showModal();
    this.emailField.focus();
  }

  private setMode(mode: Mode): void {
    this.mode = mode;
    const registering = mode === 'register';

    this.authTitle.textContent = registering ? 'Create an account' : 'Sign in';
    this.authIntro.textContent = registering
      ? 'An account is optional. Your models stay in this browser either way.'
      : 'Signing in is optional. Your models are stored in this browser either way.';

    this.authSubmit.textContent = registering ? 'Create account' : 'Sign in';
    // Tells a password manager whether to offer a saved password or to suggest a new one.
    this.passwordField.autocomplete = registering ? 'new-password' : 'current-password';
    this.passwordHint.hidden = !registering;

    // Only enforced when creating an account. Applying it to signing in would lock out
    // anyone whose existing password predates the rule.
    if (registering) {
      this.passwordField.minLength = 10;
    } else {
      this.passwordField.removeAttribute('minlength');
    }

    this.switchPrompt.textContent = registering
      ? 'Already have an account?'
      : 'Not got an account?';
    this.switchButton.textContent = registering ? 'Sign in' : 'Create one';

    this.authMessage.textContent = '';
  }

  private async submit(): Promise<void> {
    if (this.busy) return;

    const email = this.emailField.value;
    const password = this.passwordField.value;

    this.setBusy(true);
    this.authMessage.textContent = '';

    try {
      const result =
        this.mode === 'register'
          ? await this.options.api.register(email, password)
          : await this.options.api.logIn(email, password);

      this.previous = result.previousLogin;
      this.setAccount(result.user);
      this.authDialog.close();
    } catch (error) {
      this.authMessage.textContent = explain(error);

      // A password rejected as too short leaves the address worth keeping; a wrong
      // password does not leave the password worth keeping.
      this.passwordField.value = '';
      this.passwordField.focus();
    } finally {
      this.setBusy(false);
    }
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.authSubmit.disabled = busy;
    this.switchButton.disabled = busy;
    this.authSubmit.textContent = busy
      ? 'Working…'
      : this.mode === 'register'
        ? 'Create account'
        : 'Sign in';
  }

  private openAccount(): void {
    const account = this.account;
    if (!account) return;

    this.accountEmail.textContent = account.email;
    this.accountCreated.textContent = describeMoment(account.createdAt);

    this.previousRow.hidden = this.previous === undefined;
    this.previousLogin.textContent =
      this.previous === undefined ? '' : describeMoment(this.previous);

    this.accountMessage.textContent = '';
    this.accountLogins.replaceChildren(listItem('Loading…'));
    this.accountDialog.showModal();

    void this.loadHistory();
  }

  private async loadHistory(): Promise<void> {
    try {
      const history = await this.options.api.loginHistory();

      this.accountLogins.replaceChildren(
        ...(history.length === 0
          ? [listItem('No sign-ins recorded yet.')]
          : history.map((event) => listItem(describeMoment(event.at)))),
      );
    } catch (error) {
      this.accountLogins.replaceChildren();
      this.accountMessage.textContent = explain(error);

      // A history request refused for want of a session means the session has lapsed
      // since the page loaded, so the button should stop claiming otherwise.
      if (error instanceof ApiError && error.status === 401) {
        this.setAccount(undefined);
        this.accountDialog.close();
      }
    }
  }

  private async signOut(): Promise<void> {
    this.signOutButton.disabled = true;

    try {
      await this.options.api.logOut();
    } catch (error) {
      // Anything the server says other than "I could not be reached" means the session is
      // not usable anyway, so treating it as signed out is both true and what was asked
      // for. Only an unreachable service leaves the session genuinely intact.
      if (error instanceof UnreachableError) {
        this.accountMessage.textContent = explain(error);
        this.signOutButton.disabled = false;
        return;
      }
      log.error('Signing out was refused; treating the session as ended anyway.', error);
    }

    this.previous = undefined;
    this.setAccount(undefined);
    this.signOutButton.disabled = false;
    this.accountDialog.close();
  }
}

function listItem(text: string): HTMLLIElement {
  const item = document.createElement('li');
  item.textContent = text;
  return item;
}

/**
 * Turn a failure into something worth showing a person.
 *
 * The API's own messages are written to be shown, so they are used as they come; the
 * additions here are for the cases it cannot know about.
 */
function explain(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.retryAfterSeconds !== undefined) {
      return `${error.message} (about ${describeWait(error.retryAfterSeconds)})`;
    }
    return capitalise(error.message);
  }

  if (error instanceof UnreachableError) {
    return 'Could not reach the account service. Check your connection and try again.';
  }

  log.error('An unrecognised failure from the account service.', error);
  return 'Something went wrong. Please try again.';
}

/**
 * How long a call took, rounded to whole milliseconds.
 *
 * Worth having on both the success and the failure line: "did not answer" covers both a
 * refusal that came back instantly and a timeout that took thirty seconds, and those two
 * point at quite different things.
 */
function elapsed(started: number): string {
  return `${Math.round(performance.now() - started).toString()} ms`;
}

function describeWait(seconds: number): string {
  if (seconds < 90) return `${Math.ceil(seconds).toString()} seconds`;
  return `${Math.ceil(seconds / 60).toString()} minutes`;
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
